
import React, { useState } from "react";
import { MentorWithDetails } from "@/types/mentor";
import { DragDropContext, DropResult } from "react-beautiful-dnd";
import { updateOfferingsDisplayOrder } from "@/services/mentorOfferingsService";
import { useToast } from "@/components/ui/use-toast";
import { SortableHeader, EmptyOfferings, SortableList } from "./sortable-offerings";

interface MentorOfferingsSortableSectionProps {
  mentor: MentorWithDetails;
  onSaveComplete: () => void;
  onCancel: () => void;
}

// Define a type that includes the display_order property
interface OfferingWithOrder {
  id: string;
  title: string;
  description: string;
  price_cents: number;
  duration_minutes: number;
  is_active: boolean;
  display_order: number;
  game?: { id: string; name: string } | null;
  cover_image_url?: string | null;
}

export const MentorOfferingsSortableSection: React.FC<MentorOfferingsSortableSectionProps> = ({ 
  mentor, 
  onSaveComplete,
  onCancel
}) => {
  const { toast } = useToast();
  const [offerings, setOfferings] = useState<OfferingWithOrder[]>(
    mentor.offerings?.map((offering, index) => ({
      ...offering,
      display_order: offering.display_order || index + 1,
    })) || []
  );
  const [isSaving, setIsSaving] = useState(false);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    
    const items = Array.from(offerings);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    // Update display order for all items
    const updatedItems = items.map((item, index) => ({
      ...item,
      display_order: index + 1
    }));
    
    setOfferings(updatedItems);
  };

  const handleSaveOrder = async () => {
    setIsSaving(true);
    
    try {
      const orderUpdates = offerings.map((offering, index) => ({
        id: offering.id,
        display_order: index + 1
      }));
      
      const success = await updateOfferingsDisplayOrder(orderUpdates);
      
      if (success) {
        toast({
          title: "Ordem atualizada",
          description: "A ordem das mentorias foi salva com sucesso.",
        });
        onSaveComplete();
      } else {
        toast({
          title: "Erro ao salvar",
          description: "Não foi possível salvar a ordem das mentorias.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error saving order:", error);
      toast({
        title: "Erro ao salvar",
        description: "Ocorreu um erro inesperado ao salvar a ordem.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const moveItem = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) || 
      (direction === 'down' && index === offerings.length - 1)
    ) {
      return;
    }
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const items = Array.from(offerings);
    const [movedItem] = items.splice(index, 1);
    items.splice(newIndex, 0, movedItem);
    
    // Update display order for all items
    const updatedItems = items.map((item, idx) => ({
      ...item,
      display_order: idx + 1
    }));
    
    setOfferings(updatedItems);
  };

  return (
    <>
      <SortableHeader 
        isSaving={isSaving}
        onSaveOrder={handleSaveOrder}
        onCancel={onCancel}
      />

      {offerings.length === 0 ? (
        <EmptyOfferings />
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <SortableList 
            offerings={offerings}
            moveItem={moveItem}
          />
        </DragDropContext>
      )}
    </>
  );
};
